import { useState } from "react";
import { api, adminAuth } from "../../lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";

export default function ColorsSizesEditor({ product, onSaved }) {
  const [open, setOpen] = useState(false);
  const [colors, setColors] = useState(() => (product.colors || []).map((c) => ({ ...c })));
  const [sizes, setSizes] = useState(() => [...(product.sizes || [])]);
  const [sizeInput, setSizeInput] = useState("");
  const [saving, setSaving] = useState(false);

  const addColor = () => setColors((c) => [...c, { name: "", hex: "#1B2A41" }]);
  const updateColor = (i, key, val) => setColors((c) => c.map((x, idx) => idx === i ? { ...x, [key]: val } : x));
  const removeColor = (i) => setColors((c) => c.filter((_, idx) => idx !== i));

  const addSize = () => {
    const s = sizeInput.trim();
    if (!s || sizes.includes(s)) { setSizeInput(""); return; }
    setSizes((z) => [...z, s]);
    setSizeInput("");
  };
  const removeSize = (s) => setSizes((z) => z.filter((x) => x !== s));

  const save = async () => {
    setSaving(true);
    const cleanColors = colors.filter((c) => c.name.trim() && c.hex.trim());
    try {
      await api.patch(`/admin/products/${product.id}`, { colors: cleanColors, sizes }, { headers: adminAuth() });
      onSaved({ colors: cleanColors, sizes });
      toast.success("Colors & sizes saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 pt-3 border-t border-[rgba(27,42,65,0.06)]">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-[var(--ink-soft)] hover:text-[var(--navy)]"
        data-testid={`admin-attrs-toggle-${product.id}`}
      >
        {open ? "▾" : "▸"} Colors &amp; Sizes ({product.colors?.length || 0} colors · {product.sizes?.length || 0} sizes)
      </button>
      {open && (
        <div className="mt-3 space-y-4" data-testid={`admin-attrs-editor-${product.id}`}>
          <div>
            <div className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--navy)] mb-2">Colors</div>
            <div className="space-y-2">
              {colors.map((c, i) => (
                <div key={i} className="flex items-center gap-2" data-testid={`admin-color-row-${product.id}-${i}`}>
                  <input
                    type="color"
                    value={/^#([0-9a-fA-F]{3}){1,2}$/.test(c.hex) ? c.hex : "#000000"}
                    onChange={(e) => updateColor(i, "hex", e.target.value)}
                    className="w-9 h-9 rounded-sm border border-[rgba(27,42,65,0.2)] cursor-pointer bg-transparent p-0.5"
                    data-testid={`admin-color-hex-${product.id}-${i}`}
                  />
                  <input
                    type="text"
                    value={c.name}
                    placeholder="Color name"
                    onChange={(e) => updateColor(i, "name", e.target.value)}
                    className="input-field flex-1 text-sm"
                    data-testid={`admin-color-name-${product.id}-${i}`}
                  />
                  <button type="button" onClick={() => removeColor(i)} className="text-[var(--crimson)] p-1.5 hover:bg-[var(--crimson)]/10 rounded-sm" data-testid={`admin-color-remove-${product.id}-${i}`}>
                    <X className="w-4 h-4"/>
                  </button>
                </div>
              ))}
              <button type="button" onClick={addColor} className="btn-ghost text-xs py-1.5 px-3" data-testid={`admin-color-add-${product.id}`}>+ Add color</button>
            </div>
          </div>
          <div>
            <div className="font-mono-ui text-[10px] tracking-[0.2em] uppercase text-[var(--navy)] mb-2">Sizes</div>
            <div className="flex flex-wrap items-center gap-2">
              {sizes.map((s) => (
                <span key={s} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-[var(--navy)] text-[var(--bone)] font-mono-ui text-xs" data-testid={`admin-size-chip-${product.id}-${s}`}>
                  {s}
                  <button type="button" onClick={() => removeSize(s)} className="hover:text-[var(--gold)]" data-testid={`admin-size-remove-${product.id}-${s}`}>
                    <X className="w-3 h-3"/>
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={sizeInput}
                onChange={(e) => setSizeInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSize(); } }}
                placeholder="Add size (e.g. XL)"
                className="input-field w-36 text-sm"
                data-testid={`admin-size-input-${product.id}`}
              />
              <button type="button" onClick={addSize} className="btn-ghost text-xs py-1.5 px-3" data-testid={`admin-size-add-${product.id}`}>Add</button>
            </div>
          </div>
          <button type="button" onClick={save} disabled={saving} className="btn-primary btn-crimson text-xs py-2 px-5" data-testid={`admin-attrs-save-${product.id}`}>
            {saving ? "Saving…" : "Save Colors & Sizes"}
          </button>
        </div>
      )}
    </div>
  );
}
